import "react-datepicker/dist/react-datepicker.css";
import { useEffect, useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  GridApi,
  ColGroupDef,
  ValueFormatterParams,
  SizeColumnsToContentStrategy,
} from "ag-grid-enterprise";
import useSidebar from "src/hooks/useSidebar";
import BaseGrid from "../../BaseGrid";
import { downloadFile } from "src/utils/DownloadFile";
import useReport from "src/hooks/useReport";
import LoadingComponent from "src/components/LoadingComponent";
import { PulseSettlementReconciliationModel } from "src/models/PulseSettlementReconciliationModel";
import SearchCriteriaRedReports from "src/components/SearchCriteriaRedReports";
import { formatCurrency } from "src/utils/FormatCurrency";
import { AgBarSeriesOptions, AgChartOptions } from "ag-charts-enterprise";
import { PulseSettlementReconciliationSummaryModel } from "src/models/PulseSettlementReconciliationSummaryModel";
import useTheme from "src/hooks/useTheme";
import { AgCharts } from "ag-charts-react";
import useReference from "src/hooks/useReference";

type ChartData = { date: string; totalSettlement: number };

const PulseSettlementReconciliation = () => {
  const { setIsOpen } = useSidebar();
  const { theme } = useTheme();
  const { reportInputs, mapReportRequest, getContextMenuItems } = useReport();
  const { currencyList } = useReference();
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [gridReady, setGridReady] = useState(false);
  const [dataSource, setDataSource] = useState<IServerSideDatasource>();
  const [gridApi, setGridApi] = useState<GridApi>();
  const [isDownload, setIsDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);


  useEffect(() => {
    const nextOptions = { ...options };
    nextOptions.theme = theme === "dark" ? "ag-material-dark" : "ag-material";
    setOptions(nextOptions);
  }, [theme]);

  const handleViewReport = () => {
    setIsDownload(false);
    setGridReady(true);
    setDataSource(createServerSideDatasource());
  };

  const handleDownloadReport = () => {
    setIsDownload(true);
    setIsLoading(true);
    ReportApi.exportPulseSettlementReconciliation(mapReportRequest(null))
      .then((res) => {
        downloadFile(PulseSettlementReconciliation.displayName, res.data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };


// Currency formatting
type CurrencyFormatMode = "summary" | "normal";

interface CurrencyFormatOpts {
  alphaCode?: string;
  numericCode?: string | number;
  numericToAlpha?: Record<string, string>;
  mode?: CurrencyFormatMode;
}

const numericToAlpha = useMemo<Record<string, string>>(() => {
  const map: Record<string, string> = {};
  (currencyList ?? []).forEach((c: any) => {
    if (c?.numericCode != null && c?.alphaCode) {
      map[String(c.numericCode)] = String(c.alphaCode);
    }
  });
  return map;
}, [currencyList]);

const formatAmountCore = (
  value: number,
  { alphaCode = "USD", mode = "normal" }: CurrencyFormatOpts = {}
): string => {
  if (Number.isNaN(value)) return "";
  if (value === 0) return "-";

  const unsigned = String(formatCurrency(Math.abs(value), alphaCode));

  if (value < 0) {
    return mode === "summary" ? `-${unsigned}` : `(${unsigned})`;
  }
  return unsigned;
};

const resolveAlphaFromRow = (
  data: { alphaCode?: string; currencyCode?: string | number } | undefined
): string | undefined => {
  const alpha = data?.alphaCode?.trim();
  if (alpha) return alpha;

  const numeric = data?.currencyCode;
  if (numeric != null) {
    const mapped = numericToAlpha[String(numeric)];
    if (mapped) return mapped;
  }
  return undefined;
};

const currencyFormatter = (
  arg1:
    | ValueFormatterParams<PulseSettlementReconciliationModel, any>
    | string
    | number
    | null
    | undefined,
  opts?: CurrencyFormatOpts
): string => {
  // AG Grid signature (params object with 'value')
  if (arg1 && typeof arg1 === "object" && "value" in arg1) {
    const params = arg1 as ValueFormatterParams<PulseSettlementReconciliationModel, any>;
    if (params.value == null) return "";

    const value = Number(params.value);
    const isSummaryRow = params.node?.rowPinned === "bottom";
    const mode: CurrencyFormatMode = isSummaryRow ? "summary" : "normal";

    // Resolve alpha from row (alphaCode first, then numeric via currencyList)
    const alpha = resolveAlphaFromRow(params.data) ?? "USD";

    return formatAmountCore(value, { alphaCode: alpha, mode });
  }

  const raw = arg1 as string | number | null | undefined;
  if (raw == null) return "";
  const value = Number(raw);
  if (Number.isNaN(value)) return "";

  const alpha =
    opts?.alphaCode ??
    (opts?.numericCode != null
      ? (opts?.numericToAlpha ?? numericToAlpha)[String(opts.numericCode)]
      : undefined) ??
    "USD";

  return formatAmountCore(value, { ...opts, alphaCode: alpha });
};

  const [colDefs] = useState<
    | ColDef<PulseSettlementReconciliationModel>[]
    | ColGroupDef<PulseSettlementReconciliationModel>[]
  >([
    {
      headerName: "Files Processed",
      children: [
        {
          headerName: "",
          children: [
            { headerName: "Currency Code", field: "currencyCode", wrapHeaderText: true, autoHeaderHeight: true, minWidth: 80 },
            { headerName: "Clearing Date", field: "clearingDate", wrapHeaderText: true, autoHeaderHeight: true, minWidth: 120 },
            { headerName: "Clearing Cycle", field: "clearingCycle", wrapHeaderText: true, autoHeaderHeight: true, minWidth: 100 },
            { headerName: "Bin", field: "bin", wrapHeaderText: true, autoHeaderHeight: true, minWidth: 100 },
            { headerName: "PRC ID", field: "prcId", wrapHeaderText: true, autoHeaderHeight: true, minWidth: 120 },
          ],
        },
      ],
    },

    {
      headerName: "Pulse Data",
      headerStyle: { textAlign: "center" },
      children: [
        {
          headerName: "Reconciliation",
          children: [
            { headerName: "Amount Net Transaction", field: "amtNetTransaction", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 140 },
            { headerName: "Income", field: "income", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 100 },
            { headerName: "Income Expense", field: "amtIncomeExpense", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 130 },
            { headerName: "Amount Net Total", field: "amtNetTotal", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 130 },
          ],
        },

        {
          headerName: "Presentments",
          children: [
            { headerName: "Count", field: "cardCount", wrapHeaderText: true, autoHeaderHeight: true, minWidth: 90 },
            { headerName: "Amount", field: "amount", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 110 },
            ],
        },

        {
          headerName: "Non-Cardholder",
          wrapHeaderText: true,
          autoHeaderHeight: true,
          children: [
          { headerName: "Income", field: "income", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 100 },
            { headerName: "Income Expense", field: "incomeExpense", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 130 },
            { headerName: "Fees", field: "fees", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 100 },
          ],
        },
        {
          headerName: "",
          wrapHeaderText: true,
          autoHeaderHeight: true,
          children: [
            { headerName: "Total Settlement", field: "totalSettlement", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 140 }
          ],
        },
      ],
    },

    {
      headerName: "StoreFinancial System Data",
      children: [
        {
          headerName: "Presentments",
          children: [
            { headerName: "Count", field: "sfCount", minWidth: 90 },
            { headerName: "Client Programs", field: "externalSettlementAmt", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 140 },
            { headerName: "Other Non-Cardholder Settlement Amount", field: "sfOtherNonCardHolderAmount", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 180 },
            { headerName: "Internal Programs", field: "internalSettlementAmt", wrapHeaderText: true, autoHeaderHeight: true,  cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 140 },
            { headerName: "Balance Inquiry", field: "balanceInq", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 130 },
            { headerName: "Total Processed", field: "sfTotalProcessed", wrapHeaderText: true, autoHeaderHeight: true, cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 140 },
          ],
        },
        {
          headerName: "",
          children: [
            { headerName: "Discrepancy", field: "discrepancy", cellClassRules: { "cell-negative-value": (p) => p.value < 0 }, minWidth: 120 },
          ],
        },
      ],
    },
  ]);

  const defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    cellStyle: { textAlign: "center" },
  };

  const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
    return {
      type: "fitCellContents",
      defaultMinWidth: 60,
      defaultMaxWidth: 180,
      skipHeader: true,
    };
  }, []);

  const createServerSideDatasource: () => IServerSideDatasource = () => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getReportData(params);
      },
    };
  };

const getReportData = (params: IServerSideGetRowsParams | null) => {
  setIsOpen(false);

  ReportApi.getPulseSettlementReconciliation(mapReportRequest(params))
    .then((res) => {
      const result = res.data;

      // Normalize date for grid display
      result?.rows?.forEach((x: PulseSettlementReconciliationModel) => {
        x.clearingDate = x.clearingDate
          ? new Date(x.clearingDate).toLocaleDateString()
          : x.clearingDate;
      });

      // Push page rows into grid
      const successParams: LoadSuccessParams = {
        rowData: result.rows,
        rowCount: result.totalCount ?? result.rows?.length ?? 0,
      };

      if ((result?.rows?.length ?? 0) > 0) {
        params?.api?.hideOverlay();
        params?.success(successParams);
      } else {
        params?.api?.showNoRowsOverlay();
        params?.success({ rowData: [], rowCount: 0 });
      }

      const cd = mapChartData(result.summaryDetail);
      setChartData(cd);
      setOptions((prevOptions) => ({
        ...prevOptions,
        data: cd,
      }));

      // pinned bottom row
      if (result.summaryDetail?.summaryTotals) {
        const t = result.summaryDetail.summaryTotals;
        const currNumCode = result.rows[0].currencyCode;
        
        const summaryRow = {
          currencyCode: formatNumericAlphaLabel(currNumCode),
          clearingDate: "",
          clearingCycle: 0,
          bin: "",
          prcId: "",

          amtNetTransaction: t.totalAmountNetTransaction,

          amtNetFees: currencyFormatter( t.totalAmountNetFees, {  numericCode: currNumCode, mode: "summary" }),
          amtNetTotal: currencyFormatter( t.totalAmountNetTotal, { numericCode: currNumCode, mode: "summary" }),

          amount: t.totalAmount,
          fees: t.totalFees,
          totalSettlement: t.totalSettlement,

          cardCount: t.totalCardCount,
          income:  currencyFormatter( t.totalIncome, { numericCode: currNumCode, mode: "summary" }),
          incomeExpense: currencyFormatter( t.totalIncomeExpense , { numericCode: currNumCode, mode: "summary" }),

          sfCount: t.totalSFCount,
          externalSettlementAmt:currencyFormatter( t.totalExternalSettlementAmt, { numericCode: currNumCode, mode: "summary" }),
          

          sfOtherNonCardHolderAmount: currencyFormatter(t.totalSFOtherNonCardAmt, { numericCode: currNumCode, mode: "summary" }),
          internalSettlementAmt: currencyFormatter(t.totalInternalSettlementAmt, { numericCode: currNumCode, mode: "summary" }) ,
          balanceInq: currencyFormatter(t.totalBalanceInquiry, {  numericCode: currNumCode, mode: "summary" }),
          sfTotalProcessed: currencyFormatter(t.totalSFTotalProcessed, {  numericCode: currNumCode, mode: "summary" }),

          discrepancy: currencyFormatter(t.totalDiscrepancy, { numericCode: currNumCode, mode: "summary" })
          
        };

        (params?.api as any).setGridOption('pinnedBottomRowData', [summaryRow]);

      } else {
        (params?.api as any).setGridOption('pinnedBottomRowData', []);
      }
    })
    .catch(() => {
      params?.fail();
        (params?.api as any).setGridOption('pinnedBottomRowData', []);
    });
};



 // For "840-USD" formatting
const formatNumericAlphaLabel = (numericCode: string | number): string => {
  if (numericCode == null) return "";

  const target = String(numericCode).trim().padStart(3, "0");

  // Loop through currencyList to find a matching numeric code
  for (const c of currencyList ?? []) {
    const num = String((c as any).numericCode).trim().padStart(3, "0");
    if (num === target) {
      const alpha = String((c as any).alphaCode ?? "").trim().toUpperCase();
      return alpha ? `${target}-${alpha}` : target;
    }
  }

  // Fallback: show just the numeric code if not found in the list
  return target;
};


  const mapChartData = (
    data: PulseSettlementReconciliationSummaryModel
  ): ChartData[] => {
    const points: ChartData[] = [];
    (data?.dailyTotals ?? []).forEach((t) => {
      const date = t.clearingDate
        ? new Date(t.clearingDate).toLocaleDateString()
        : "";
      points.push({ date, totalSettlement: t.totalSettlement ?? 0 });
    });
    return points;
  };

  const [options, setOptions] = useState<AgChartOptions>({
    title: {
      text: "Total Settlement by Date",
    },
    data: chartData as ChartData[],
    series: [
      {
        type: "bar",
        xKey: "date",
        yKey: "totalSettlement",
      } as AgBarSeriesOptions,
    ],
    theme: theme === "dark" ? "ag-material-dark" : "ag-material",
  });

  return (
    <>
      <div className="report-container">
        <div className="report-header">
          <h5 className="mb-0">Pulse Settlement Reconciliation</h5>
          {reportInputs?.fromDate && reportInputs?.toDate && (
            <div className="report-header-date">
              {`${reportInputs.fromDate.toLocaleDateString()} - ${reportInputs.toDate.toLocaleDateString()}`}
            </div>
          )}
        </div>

        <div className="blocks">
          <SearchCriteriaRedReports
            handleViewReport={handleViewReport}
            handleDownloadReport={handleDownloadReport}
            useIcaValue={false}
            useBins={true}
          />

          {chartData?.length > 0 && !isDownload && (
            <div className="report-chart">
              <AgCharts options={options} />
            </div>
          )}
        </div>

        {isDownload && isLoading ? (
          <LoadingComponent />
        ) : (
          !isDownload && (
              <BaseGrid
                colDefs={colDefs}
                defaultColDef={defaultColDef}
                getData={getReportData}
                isGridReady={gridReady}
                reportInputs={reportInputs}
                dataSource={dataSource}
                gridApi={gridApi}
                setGridApi={setGridApi}
                getContextMenuItems={getContextMenuItems}
                autoSizeStrategy={autoSizeStrategy}

              />
          )
        )}
      </div>
    </>
  );
};

PulseSettlementReconciliation.displayName = "PulseSettlementReconciliation";
export default PulseSettlementReconciliation;
